import type { Locale } from "@/i18n/dictionaries";
import { loadDatabaseProductListDto } from "./product-data-client";
import { localizeProductListViewModel } from "./product-display-i18n";

const pageSize = 18;

export const sortValues = ["newest", "popular", "price-asc", "price-desc"] as const;
export type ProductSort = (typeof sortValues)[number];

export type ProductListSource = "database" | "unavailable";

export type ProductListFilter = {
  page: number;
  category: string;
  sort: ProductSort;
  style: string;
  key: string;
};

export type ProductListCategory = {
  value: string;
  label: string;
  rawLabel?: string;
};

export type ProductListStyle = {
  value: string;
  label: string;
  rawLabel?: string;
};

export type ProductListItem = {
  id: string;
  name: string;
  goodsType: number;
  price: number;
  designer: string;
  image: string;
  modelId?: number;
  mainPhotoId?: string;
  keyTips?: string;
  detailHref?: string;
  disabledReason?: string;
};

export type ProductListViewModel = {
  source: ProductListSource;
  message?: string | null;
  stopReason?: string;
  normalizedKey?: string;
  products: ProductListItem[];
  categories: ProductListCategory[];
  styles: ProductListStyle[];
  totalCount: number;
  pageCount: number;
  page: number;
  goodsTypeCounts: Record<string, number>;
};

export function normalizeProductSort(value?: string | string[] | null): ProductSort {
  const candidate = Array.isArray(value) ? value[0] : value;
  return sortValues.includes(candidate as ProductSort) ? (candidate as ProductSort) : "newest";
}

export function normalizeProductSearchKey(value: string) {
  return value.trim();
}

function unavailableList(locale: Locale, filter: ProductListFilter, reason: string): ProductListViewModel {
  return {
    source: "unavailable",
    message: reason,
    stopReason: reason,
    normalizedKey: normalizeProductSearchKey(filter.key),
    products: [],
    categories: [{ value: "all", label: locale === "en-US" ? "All Categories" : "全部分类" }],
    styles: [],
    totalCount: 0,
    pageCount: 1,
    page: Math.max(1, filter.page),
    goodsTypeCounts: {}
  };
}

function normalizeListFromDatabase(locale: Locale, filter: ProductListFilter, data: ProductListViewModel): ProductListViewModel {
  const hasAllCategory = data.categories.some((category) => category.value === "all");
  const categories = hasAllCategory
    ? data.categories
    : [{ value: "all", label: locale === "en-US" ? "All Categories" : "全部分类" }, ...data.categories];

  return {
    ...data,
    source: "database",
    normalizedKey: normalizeProductSearchKey(filter.key),
    categories,
    page: Math.max(1, data.page || filter.page),
    pageCount: Math.max(1, data.pageCount || Math.ceil(data.totalCount / pageSize))
  };
}

export async function loadProductList(locale: Locale, filter: ProductListFilter): Promise<ProductListViewModel> {
  const data = await loadDatabaseProductListDto({
    ...filter,
    key: normalizeProductSearchKey(filter.key)
  });

  if (!data) {
    return unavailableList(locale, filter, "database-list-unavailable");
  }

  return localizeProductListViewModel(normalizeListFromDatabase(locale, filter, data), locale);
}

export async function checkProductListHealth() {
  const data = await loadDatabaseProductListDto({
    page: 1,
    category: "all",
    sort: "newest",
    style: "all",
    key: ""
  });

  return data
    ? { ok: true, baseUrl: "database", totalCount: data.totalCount }
    : { ok: false, baseUrl: "database", message: "Database product list is unavailable." };
}
